import { TFunction } from 'i18next';
import { ImageIcon } from '@phosphor-icons/react';
import { TableColumn } from '@/types/table';
import { CameraActionsCell } from './CameraActionsCell';
import { CameraStatusTooltip } from '@/components/CameraStatusTooltip';
import { CameraThumbnailCell } from './CameraThumbnailCell';
import { CameraTableRow } from '../types';

interface ColumnHandlers {
    onEdit: (camera: CameraTableRow) => void;
    onDelete: (camera: CameraTableRow) => void;
    onRefresh: (sourceId: string) => void;
}

interface ColumnOptions {
    editedRowIds: Set<string>;
    thumbnailKeys: Record<string, number>;
    t: TFunction;
}

export function getCameraTableColumns(
    handlers: ColumnHandlers,
    options: ColumnOptions,
): TableColumn<CameraTableRow>[] {
    const { editedRowIds, thumbnailKeys, t } = options;
    return [
        {
            id: 'thumbnail',
            header: <ImageIcon className="w-4 h-4 text-grey-200" />,
            width: '104px',
            cell: ({ row }) => (
                <CameraThumbnailCell
                    sourceId={row.original.sourceId}
                    refreshKey={thumbnailKeys[row.original.sourceId]}
                />
            ),
            enableSorting: false,
        },
        {
            id: 'name',
            header: t('columns.name'),
            accessor: 'name',
            width: '180px',
            enableSorting: true,
            editable: true,
            cell: ({ value }) => (
                <span className="block w-full truncate text-sm text-white-100">
                    {String(value ?? '')}
                </span>
            ),
        },
        {
            id: 'status',
            header: '',
            width: '48px',
            cell: ({ row }) => (
                <div className="flex items-center justify-center">
                    <CameraStatusTooltip
                        sourceId={row.original.sourceId}
                        stale={editedRowIds.has(row.original.sourceId)}
                    />
                </div>
            ),
            enableSorting: false,
        },
        {
            id: 'url',
            header: t('columns.url'),
            accessor: 'streamUrl',
            editable: true,
            cell: ({ value }) => (
                <span
                    className="block w-full truncate text-sm text-white-100"
                    title={String(value ?? '')}
                >
                    {String(value ?? '')}
                </span>
            ),
            enableSorting: false,
        },
        {
            id: 'username',
            header: t('columns.username'),
            accessor: 'username',
            width: '112px',
            editable: true,
            cell: ({ value }) => (
                <span className="block w-full truncate text-sm text-white-100">
                    {String(value ?? '')}
                </span>
            ),
            enableSorting: false,
        },
        {
            id: 'password',
            header: t('columns.password'),
            accessor: 'password',
            width: '96px',
            editable: true,
            editConfig: {
                initialValue: '',
                displayValue: '******',
            },
            cell: ({ value }) => (
                <span className="block w-full truncate font-mono text-xs text-white-100">
                    {value ? String(value) : ''}
                </span>
            ),
            enableSorting: false,
        },
        {
            id: 'actions',
            header: t('columns.actions'),
            width: '128px',
            cell: ({ row }) => (
                <CameraActionsCell
                    camera={row.original}
                    onDelete={handlers.onDelete}
                    onRefresh={handlers.onRefresh}
                />
            ),
            enableSorting: false,
        },
    ];
}
